import { Warning } from '@mui/icons-material';
import { FormControl, Grid, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { ProcessStep } from '../../common/enums/enums';
import { useForm } from '../../hooks/useForm';
import { GenConfirmationDialog } from '../GenDialogs';

const Observation = ({
  subtitle,
  legend,
  noteName,
  note,
  handleInputChange,
  testId,
}) => {
  return (
    <Grid container direction="column" alignItems="center">
      <Grid item width="100%">
        <Typography
          variant="body1"
          color="textSecondary"
          textAlign="center"
          mb={3}
        >
          {subtitle}
        </Typography>
      </Grid>
      <Grid item width="100%">
        <Typography
          variant="body1"
          color="textSecondary"
          textAlign="left"
          mb={1}
        >
          {legend}
        </Typography>
      </Grid>
      <Grid item width="100%">
        <FormControl fullWidth>
          <TextField
            id="outlined-textarea"
            name={noteName}
            multiline
            minRows={4}
            variant="outlined"
            margin="dense"
            value={note}
            onChange={handleInputChange}
            sx={{ width: '100%', margin: 0 }}
            inputProps={{
              'data-testid': testId,
            }}
          />
        </FormControl>
      </Grid>
    </Grid>
  );
};

export const CancelModal = ({
  vendorOrderId,
  open,
  handleOpenState,
  statusCancel,
  setCurrentLoadingButton,
  isApiLoading,
  woNumber,
}) => {
  const { t } = useTranslation('workOrderStepper');
  const [{ cancellingNote }, handleInputChange, reset] = useForm({
    cancellingNote: '',
  });
  const [disabled, setDisabled] = useState(true);
  useEffect(() => {
    setDisabled(!cancellingNote.length);
  }, [cancellingNote]);

  const handleCloseClick = useCallback(() => {
    handleOpenState(false);
    setCurrentLoadingButton(null);
  }, [handleOpenState, setCurrentLoadingButton]);

  const handleOnConfirm = () => {
    statusCancel({ vendorOrderId, cancellingNote });
    reset();
  };

  useEffect(() => {
    if (!isApiLoading) {
      handleCloseClick();
    }
  }, [isApiLoading, handleCloseClick]);

  return (
    <GenConfirmationDialog
      open={open}
      title={t('wOStepperCancelPopUp.title')}
      titleIcon={Warning}
      titleIconColor="warning"
      closeText={t('wOStepperCancelPopUp.close')}
      disabled={disabled}
      onClose={handleCloseClick}
      onRefuse={handleCloseClick}
      confirmText={t('wOStepperCancelPopUp.confirm')}
      onConfirm={handleOnConfirm}
      isLoading={isApiLoading}
      children={
        <Observation
          subtitle={
            <Trans i18nKey="wOStepperCancelPopUp.subtitle">
              {t('wOStepperCancelPopUp.subtitle', { value: woNumber })}
            </Trans>
          }
          legend={t('wOStepperCancelPopUp.legend')}
          noteName={'cancellingNote'}
          note={cancellingNote}
          handleInputChange={handleInputChange}
          testId={'cancelTextArea'}
        />
      }
    />
  );
};

export const PauseModal = ({
  vendorOrderId,
  open,
  handleOpenState,
  statusChange,
  setCurrentLoadingButton,
  isApiLoading,
}) => {
  const { t } = useTranslation('workOrderStepper');

  const [{ pausingNote }, handleInputChange, reset] = useForm({
    pausingNote: '',
  });
  const [disabled, setDisabled] = useState(true);
  useEffect(() => {
    setDisabled(!pausingNote.length);
  }, [pausingNote]);

  const handleCloseClick = useCallback(() => {
    handleOpenState(false);
    setCurrentLoadingButton(null);
  }, [handleOpenState, setCurrentLoadingButton]);

  const handleOnConfirm = () => {
    statusChange(ProcessStep.PAUSED, vendorOrderId, pausingNote);
    reset();
  };

  useEffect(() => {
    if (!isApiLoading) {
      handleCloseClick();
    }
  }, [isApiLoading, handleCloseClick]);

  return (
    <GenConfirmationDialog
      open={open}
      title={t('wOStepperPausePopUp.title')}
      titleIcon={Warning}
      titleIconColor="warning"
      closeText={t('wOStepperPausePopUp.close')}
      disabled={disabled}
      onClose={handleCloseClick}
      onRefuse={handleCloseClick}
      confirmText={t('wOStepperPausePopUp.confirm')}
      onConfirm={handleOnConfirm}
      isLoading={isApiLoading}
      children={
        <Observation
          subtitle={t('wOStepperPausePopUp.subtitle')}
          legend={t('wOStepperPausePopUp.legend')}
          noteName={'pausingNote'}
          note={pausingNote}
          handleInputChange={handleInputChange}
          testId={'pauseTextArea'}
        />
      }
    />
  );
};
